import React, { useEffect, useState } from 'react';
import { getAdminMissions, getMissionDetails } from '../api/api';
import styles from './AdminLists.module.css';
import FilterBar from '../components/FilterBar';

const MissionsPage = () => {
    const [missions, setMissions] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, total_pages: 1 });
    const [loadingList, setLoadingList] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeSearch, setActiveSearch] = useState('');

    // Filtres
    const [filters, setFilters] = useState({
        status: '',
        tjmMin: '',
        tjmMax: '',
        dateDebut: '',
        dateFin: '',
    });

    const [showModal, setShowModal] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [details, setDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [activeTab, setActiveTab] = useState('infos');

    const loadMissionsData = async (targetPage, currentSearch, currentFilters) => {
        setLoadingList(true);
        try {
            const response = await getAdminMissions(targetPage, 10, currentSearch, currentFilters);
            setMissions(response.data.data);
            setPagination(response.data.pagination);
        } catch (err) {
            console.error("Erreur API missions", err);
        } finally {
            setLoadingList(false);
        }
    };

    // 1. UNIQUE SOURCE DE VÉRITÉ : Le useEffect gère tout.
    useEffect(() => {
        loadMissionsData(pagination.page, activeSearch, filters);
    }, [pagination.page, filters, activeSearch]);

    // 2. Gestionnaires simplifiés (On ne fait QUE changer le State)
    const handleFilterChange = (name, value) => {
        // Si on change un filtre, on revient TOUJOURS à la page 1
        setPagination(prev => ({ ...prev, page: 1 })); 

        if (name === 'tjm') {
            setFilters(prev => ({ ...prev, tjmMin: value.min || '', tjmMax: value.max || '' }));
        } else {
            setFilters(prev => ({ ...prev, [name]: value }));
        }
        // L'APPEL API SE FERA AUTOMATIQUEMENT VIA LE USEEFFECT
    };

    const handleSearch = (e) => {
        if(e) e.preventDefault();
        setPagination(prev => ({ ...prev, page: 1 }));
        setActiveSearch(searchTerm); // C'est ici que l'appel est "autorisé"
    };

    const handleResetFilters = () => {
        setSearchTerm('');
        setActiveSearch('');
        setFilters({ status: '', tjmMin: '', tjmMax: '', dateDebut: '', dateFin: '' });
        setPagination(prev => ({ ...prev, page: 1 }));
        // L'APPEL API SE FERA AUTOMATIQUEMENT VIA LE USEEFFECT
    };

    const filterConfig = [
        {
            type: 'select',
            name: 'status',
            label: 'Statut',
            value: filters.status,
            options: [
                { value: 'en cours', label: 'En cours' },
                { value: 'à venir', label: 'À venir' },
                { value: 'terminée', label: 'Terminée' },
            ]
        },
        {
            type: 'number',
            name: 'tjm',
            label: 'TJM (€)',
            min: filters.tjmMin,
            max: filters.tjmMax,
        },
        {
            type: 'date',
            name: 'dateDebut',
            label: 'Début après le',
            value: filters.dateDebut,
        },
        {
            type: 'date',
            name: 'dateFin',
            label: 'Fin avant le',
            value: filters.dateFin,
        },
    ];

    const handleOpenDetails = async (id) => {
        setSelectedId(id);
        setShowModal(true);
        setActiveTab('infos');
        setLoadingDetails(true);
        try {
            const res = await getMissionDetails(id);
            setDetails(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingDetails(false);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedId(null);
        setDetails(null);
    };

    const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';
    const formatCurrency = (amount) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
    
    const getMissionStatus = (start, end) => {
        const now = new Date();
        const startDate = new Date(start);
        const endDate = new Date(end);

        if (now < startDate) {
            return { label: 'À venir', class: styles.badgeInfo };
        } else if (now >= startDate && now <= endDate) {
            return { label: 'En cours', class: styles.badgeSuccess };
        } else {
            return { label: 'Terminée', class: styles.badgeSecondary || styles.badgeDanger };
        }
    };
    
    const getInvoiceStateBadge = (state) => {
        const s = (state || '').toLowerCase();
        if (s.includes('paid') || s.includes('payé')) return styles.badgeSuccess;
        if (s.includes('sent') || s.includes('envoyé')) return styles.badgeInfo;
        if (s.includes('late') || s.includes('retard')) return styles.badgeDanger;
        return styles.badgeWarning;
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>Gestion des Missions</h1>
                <div className={styles.searchForm}>
                    <input 
                        type="text" 
                        placeholder="Rechercher (Partenaire, Consultant)..." 
                        className={styles.searchInput}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
                    />
                    <button onClick={handleSearch} className={styles.searchBtn}>🔍</button>
                </div>
            </div>

            <FilterBar 
                filters={filterConfig}
                onFilterChange={handleFilterChange}
                onReset={handleResetFilters}
            />

            <div className={styles.tableWrapper}>
                {loadingList ? <div className={styles.loading}>Chargement...</div> : (
                    <>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Consultant</th>
                                    <th>Partenaire</th>
                                    <th>Début</th>
                                    <th>Fin</th>
                                    <th>TJM</th>
                                    <th>Statut</th>
                                    <th style={{textAlign:'center'}}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {missions.map((m) => {
                                    const status = getMissionStatus(m.startDate, m.endDate);
                                    return (
                                        <tr key={m.prestation_id}>
                                            <td className={styles.fwBold}>{m.resourceLastName} {m.resourceFirstName}</td>
                                            <td>{m.clientName}</td>
                                            <td>{formatDate(m.startDate)}</td>
                                            <td>{formatDate(m.endDate)}</td>
                                            <td>{m.TJM} €</td>
                                            <td>
                                                <span className={`${styles.badge} ${status.class}`}>
                                                    {status.label}
                                                </span>
                                            </td>
                                            <td style={{textAlign:'center'}}>
                                                <button className={styles.btnDetail} onClick={() => handleOpenDetails(m.prestation_id)}>
                                                    Détails
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {missions.length === 0 && (
                                    <tr>
                                        <td colSpan="8" style={{textAlign: 'center', padding: '40px'}}>
                                            Aucune mission trouvée avec ces critères
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        
                        <div className={styles.pagination}>
                            <button 
                                disabled={pagination.page === 1}
                                onClick={() => setPagination({...pagination, page: pagination.page - 1})}
                                className={styles.pageBtn}
                            >Précédent</button>
                            <span className={styles.pageInfo}>Page {pagination.page} / {pagination.total_pages}</span>
                            <button 
                                disabled={pagination.page === pagination.total_pages}
                                onClick={() => setPagination({...pagination, page: pagination.page + 1})}
                                className={styles.pageBtn}
                            >Suivant</button>
                        </div>
                    </>
                )}
            </div>

            {showModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalContent} style={{ maxWidth: '900px' }}>
                        <button className={styles.closeBtn} onClick={handleCloseModal}>&times;</button>
                        
                        {loadingDetails || !details ? <div className={styles.loading}>Chargement...</div> : (
                            <>
                                <h2 className={styles.modalTitle}>
                                    Mission #{details.info.order_id}
                                    <span style={{fontSize:'0.6em', color:'#666', marginLeft:'10px'}}>
                                        ({details.info.clientName})
                                    </span>
                                </h2>

                                <div className={styles.tabs}>
                                    <button 
                                        className={`${styles.tab} ${activeTab === 'infos' ? styles.activeTab : ''}`} 
                                        onClick={() => setActiveTab('infos')}
                                    >Informations</button>
                                    <button 
                                        className={`${styles.tab} ${activeTab === 'paiements' ? styles.activeTab : ''}`} 
                                        onClick={() => setActiveTab('paiements')}
                                    >Paiements & Factures ({details.invoices.length})</button>
                                </div>

                                <div className={styles.tabContent}>
                                    {activeTab === 'infos' && (
                                        <div className={styles.infoGrid}>
                                            <div className={styles.infoItem}>
                                                <label>Consultant</label>
                                                <span>{details.info.firstName} {details.info.lastName}</span>
                                                <small>{details.info.resourceEmail}</small>
                                            </div>
                                            <div className={styles.infoItem}>
                                                <label>Partenaire</label>
                                                <span>{details.info.clientName}</span>
                                                <small>SIRET: {details.info.siret || '-'}</small>
                                            </div>
                                            <div className={styles.infoItem}>
                                                <label>Période</label>
                                                <span>Du {formatDate(details.info.startDate)} au {formatDate(details.info.endDate)}</span>
                                            </div>
                                            <div className={styles.infoItem}>
                                                <label>TJM Vendu</label>
                                                <span style={{fontSize:'1.2rem', fontWeight:'bold', color:'#2c3e50'}}>
                                                    {details.info.TJM} €
                                                </span>
                                            </div>
                                            <div className={styles.infoItem}>
                                                <label>Jours Travaillés (Simulé)</label>
                                                <span>{details.info.numberOfWorkingDays || 0} jours</span>
                                            </div>
                                            <div className={styles.infoItem}>
                                                <label>CA Simulé HT</label>
                                                <span>{formatCurrency(details.info.turnoverSimulatedExcludingTax)}</span>
                                            </div>
                                        </div>
                                    )}

                                    {activeTab === 'paiements' && (
                                        <div style={{overflowX: 'auto'}}>
                                            <table className={styles.tableSmall}>
                                                <thead>
                                                    <tr>
                                                        <th>Mois Facture</th>
                                                        <th>Détail Normal (HT / Jours)</th>
                                                        <th>Détail HNO (HT / Jours)</th>
                                                        <th>Montant HT</th>
                                                        <th>Montant TTC</th>
                                                        <th>Date Prévue Paiement</th>
                                                        <th>État</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {details.invoices.map((inv, idx) => (
                                                        <tr key={idx}>
                                                            <td>
                                                                {inv.factureDate ? new Date(inv.factureDate).toLocaleString('fr-FR', { month: 'long', year: 'numeric' }) : '-'}
                                                            </td>
                                                            <td>
                                                                <div style={{fontWeight:'500'}}>{formatCurrency(inv.ht_normal)}</div>
                                                                <small style={{color:'#666'}}>{inv.quantity_Normal || 0} jours</small>
                                                            </td>
                                                            <td>
                                                                <div style={{color: inv.ht_hno > 0 ? '#27ae60' : '#666'}}>
                                                                    {formatCurrency(inv.ht_hno)}
                                                                </div>
                                                                <small style={{color:'#666'}}>{inv.quantity_HNO || 0} jours</small>
                                                            </td>
                                                            <td>{formatCurrency(inv.montant_ht)}</td>
                                                            <td>{formatCurrency(inv.montant_ttc)}</td>
                                                            <td>{formatDate(inv.expectedPaymentDate)}</td>
                                                            <td>
                                                                <span className={`${styles.badge} ${getInvoiceStateBadge(inv.stateFacture)}`}>
                                                                    {inv.stateFacture || 'Inconnu'}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {details.invoices.length === 0 && (
                                                        <tr>
                                                            <td colSpan="7" style={{textAlign:'center', padding:'20px'}}>
                                                                Aucune facture émise pour cette mission.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MissionsPage;